import type { SegmentsOptions } from '@openx/types/targeting/customVariables';
import {
  type ContextualSegmentIdentity,
  getContextualSegmentKey,
  normalizeProviderId,
  resolveContextualProviderId,
} from './enrichment';

/**
 * Everything needed to judge whether a selected segment can still deliver, prepared once per render
 * so the per-segment check stays a pair of set lookups.
 */
export type ContextualMisconfigurationContext = {
  /** Normalized ids of the contextual providers currently connected to the account. */
  connectedProviderIds: Set<string>;
  /** Keys of segments the API vouches for, scoped to the provider they belong to. */
  authoritativeSegmentKeys: Set<string>;
};

export const buildContextualMisconfigurationContext = (
  segments: SegmentsOptions,
  accountProviderIds: string[]
): ContextualMisconfigurationContext => {
  const connectedProviderIds = new Set(accountProviderIds.map(normalizeProviderId).filter(Boolean));
  const authoritativeSegmentKeys = new Set<string>();

  segments.forEach(segment => {
    const providerId = resolveContextualProviderId(segment, accountProviderIds);
    if (providerId) {
      authoritativeSegmentKeys.add(getContextualSegmentKey({ providerId, segmentId: segment.id }));
    }
  });

  return { connectedProviderIds, authoritativeSegmentKeys };
};

/**
 * A segment is misconfigured when its provider is no longer connected to the account *and* the API
 * does not vouch for it — typically after an admin switches the account's contextual partner.
 *
 * This relies on the account-scoped segment lookup returning no record for segments belonging to a
 * provider the account is not connected to (verified on devint for EXCH-18667). If that lookup ever
 * starts returning those records, `authoritativeSegmentKeys` would cover them and the warning would
 * go silent.
 */
export const isContextualSegmentMisconfigured = (
  identity: ContextualSegmentIdentity,
  { connectedProviderIds, authoritativeSegmentKeys }: ContextualMisconfigurationContext
): boolean => {
  const providerId = normalizeProviderId(identity.providerId);

  if (connectedProviderIds.has(providerId)) {
    return false;
  }

  return !authoritativeSegmentKeys.has(getContextualSegmentKey({ providerId, segmentId: identity.segmentId }));
};
